import { FC, useState } from 'react'
import {
  MdArchive,
  MdOpenInNew,
  MdOutlineArchive,
  MdOutlineStarBorder,
  MdOutlineStarPurple500,
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Config, { priceForLanguage } from '../../../lib/Config'
import { Spreadsheet } from '../../../lib/Types'
import Headline from '../../Elements/Headline'
import Button from '../../Formik/Button'
import Paywall from '../../Paywall'
import TrialLabel from '../TrialLabel'
import { useUpdateSheet } from '../lib/Hooks/Sheet'
import { isOnActiveTrial, isPaymentOverdue } from '../lib/Payment'

interface Props {
  spreadsheet: Spreadsheet
}

const SpreadsheetCard: FC<Props> = ({ spreadsheet }) => {
  const { t, i18n } = useTranslation(['spreadsheet', 'common'])
  const { mutate: update } = useUpdateSheet()
  const [showPaywall, togglePaywall] = useState(false)
  const overdue = isPaymentOverdue(spreadsheet)
  const onActiveTrial = isOnActiveTrial(spreadsheet)
  const price = priceForLanguage(
    Config.PRICE_SPREADSHEET,
    i18n.language,
    Config.PRICE_SPREADSHEET_EUR,
  )
  const spreadsheetName =
    spreadsheet.name || t('spreadsheet:card.untitled', { id: spreadsheet.id })
  const purchaseName = spreadsheet.name || t('common:paywall.spreadsheet')

  const toggleFavorite = () => {
    const newSpreadsheet = structuredClone(spreadsheet)

    newSpreadsheet.is_favorite = !newSpreadsheet.is_favorite

    update(newSpreadsheet)
  }

  const toggleArchive = () => {
    const newSpreadsheet = structuredClone(spreadsheet)

    newSpreadsheet.is_archived = !newSpreadsheet.is_archived

    update(newSpreadsheet)
  }

  return (
    <div
      className={`${overdue ? 'bg-trial-ended' : onActiveTrial ? 'bg-active-trial' : 'bg-gray-100'} p-4 mt-4`}
    >
      <Link to={`/spreadsheet/${spreadsheet.id}`}>
        <div className='flex justify-between items-start'>
          <Headline size='base' className='mb-4'>
            <strong>{spreadsheetName}</strong>
          </Headline>

          {overdue
            ? (
              <div
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  togglePaywall(true)
                }}
              >
                <Button theme='gray'>
                  {t('common:paywall.pay', { price })}
                </Button>
              </div>
            )
            : (
              <Button theme='gray'>
                <div className='flex'>
                  <MdOpenInNew size='1.25rem' className='mr-2' />
                  {t('spreadsheet:card.open')}
                </div>
              </Button>
            )}
        </div>
        {spreadsheet?.description && <p>{spreadsheet!.description}</p>}
      </Link>
      <div className='flex justify-between items-center mt-2'>
        <TrialLabel
          sheet={spreadsheet}
          overdue={overdue}
          onActiveTrial={onActiveTrial}
        />
        <div className='flex items-center ml-auto'>
          {!spreadsheet.is_favorite && (
            <MdOutlineStarBorder
              size='1.5rem'
              className='mr-2 cursor-pointer'
              onClick={toggleFavorite}
              title={t('spreadsheet:card.markFavorite')}
            />
          )}
          {spreadsheet.is_favorite && (
            <MdOutlineStarPurple500
              size='1.5rem'
              className='mr-2 cursor-pointer'
              onClick={toggleFavorite}
              title={t('spreadsheet:card.unmarkFavorite')}
            />
          )}
          {!spreadsheet.is_archived && (
            <MdOutlineArchive
              size='1.5rem'
              className='cursor-pointer'
              onClick={toggleArchive}
              title={t('spreadsheet:card.archive')}
            />
          )}
          {spreadsheet.is_archived && (
            <MdArchive
              size='1.5rem'
              className='cursor-pointer'
              onClick={toggleArchive}
              title={t('spreadsheet:card.restore')}
            />
          )}
        </div>
      </div>
      <Paywall
        show={showPaywall}
        onHide={() => togglePaywall(false)}
        sheetType={spreadsheet.sheet_type}
        sheetId={spreadsheet.id}
        name={purchaseName}
      />
    </div>
  )
}

export default SpreadsheetCard
